import React, { memo, useCallback, useEffect, useState } from "react";
import { Icon, Text } from "@shopify/polaris";
import { XIcon } from "@shopify/polaris-icons";
import { apiCall } from "../../helper/Common";
import { openRemoteUrl } from "../../utils/openRemoteUrl";

const PARTNER_APPS_URL = "https://orbitsdev.com/orbits-partner-apps.json";

function dismissKey(currentAppId) {
  return `orbits-partner-apps-dismissed:${currentAppId || "app"}`;
}

function isDismissed(currentAppId) {
  try {
    return sessionStorage.getItem(dismissKey(currentAppId)) === "1";
  } catch {
    return false;
  }
}

function logoSrc(catalog, app) {
  const icon = String(app?.icon || "").trim();
  if (!icon) return "";
  if (/^https?:\/\//i.test(icon)) return icon;
  const base = String(catalog?.logoBase || "https://orbitsdev.com/app-logo/").replace(/\/+$/, "");
  return `${base}/${icon.replace(/^\/+/, "")}`;
}

function normalizeCatalog(raw, currentAppId) {
  if (!raw || raw.enabled === false || !Array.isArray(raw.apps)) return null;
  const current = String(currentAppId || "").trim().toLowerCase();
  const apps = raw.apps.filter((app) => {
    if (!app || typeof app !== "object") return false;
    const id = String(app.id || "").trim().toLowerCase();
    if (current && id === current) return false;
    if (!String(app.name || "").trim() || !String(app.url || "").trim()) return false;
    return true;
  });
  if (!apps.length) return null;
  return {
    title: String(raw.title || "Recommended apps").trim() || "Recommended apps",
    subtitle: String(raw.subtitle || "").trim(),
    ctaLabel: String(raw.cta_label || "Install app for free").trim() || "Install app for free",
    logoBase: raw.logo_base,
    apps,
  };
}

async function loadCatalog(currentAppId) {
  try {
    const res = await fetch(PARTNER_APPS_URL, { cache: "no-store" });
    if (res.ok) {
      return normalizeCatalog(await res.json(), currentAppId);
    }
  } catch {
    /* orbitsdev.com does not send Access-Control-Allow-Origin */
  }
  const data = await apiCall("public/partner-apps", "GET", null, null, { timeout: 12000 });
  return normalizeCatalog(data, currentAppId);
}

function AppIcon({ app, src }) {
  const [failed, setFailed] = useState(false);
  const letter = String(app.name || "?").trim().charAt(0).toUpperCase() || "?";
  if (!src || failed) {
    return (
      <span
        className="partner-apps__icon partner-apps__icon--fallback"
        style={{ background: app.accent || "#1a1a1a" }}
        aria-hidden
      >
        {letter}
      </span>
    );
  }
  return (
    <img
      className="partner-apps__icon"
      src={src}
      alt=""
      width={52}
      height={52}
      onError={() => setFailed(true)}
    />
  );
}

function RecommendedAppsSection({ currentAppId }) {
  const [dismissed, setDismissed] = useState(() => isDismissed(currentAppId));
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    if (dismissed) return undefined;
    let cancelled = false;
    loadCatalog(currentAppId)
      .then((next) => {
        if (!cancelled) setCatalog(next);
      })
      .catch(() => {
        if (!cancelled) setCatalog(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentAppId, dismissed]);

  const onDismiss = useCallback(() => {
    try {
      sessionStorage.setItem(dismissKey(currentAppId), "1");
    } catch {
      /* ignore private-mode storage errors */
    }
    setDismissed(true);
  }, [currentAppId]);

  const onInstall = useCallback((url) => {
    openRemoteUrl(url, { newContext: true });
  }, []);

  if (dismissed || !catalog) return null;

  return (
    <section className="partner-apps" aria-label={catalog.title}>
      <div className="partner-apps__header">
        <div>
          <Text as="h2" variant="headingMd">
            {catalog.title}
          </Text>
          {catalog.subtitle ? (
            <Text as="p" variant="bodySm" tone="subdued">
              {catalog.subtitle}
            </Text>
          ) : null}
        </div>
        <button type="button" className="partner-apps__close" onClick={onDismiss} aria-label="Dismiss recommended apps">
          <Icon source={XIcon} tone="subdued" />
        </button>
      </div>

      <div className="partner-apps__grid">
        {catalog.apps.map((app) => (
          <article key={app.id || app.url} className="partner-apps__card">
            <AppIcon app={app} src={logoSrc(catalog, app)} />
            <p className="partner-apps__name">{app.name}</p>
            <p className="partner-apps__desc">{app.description}</p>
            <button type="button" className="partner-apps__cta" onClick={() => onInstall(app.url)}>
              {catalog.ctaLabel}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

export default memo(RecommendedAppsSection);
